'use client';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useReceiptBuilder } from '@/hooks/useReceiptBuilder';
import { useAuth } from '@/components/auth/auth-provider';
import { useUpgrade } from '@/components/upgrade/UpgradeModal';
import { normalizeFont } from '@/lib/templates/defaults';
import type { PaperStyle, CurrencyFormat, SpacingStyle } from '@/lib/templates/schema';

const FONTS = [
  { value: 'Merchant Copy', label: 'Receipt 01', recommendedSize: 9 },
  { value: 'Bit Matrix A', label: 'Receipt 02', recommendedSize: 12 },
  { value: 'Bit Matrix C', label: 'Receipt 03', recommendedSize: 12 },
  { value: 'Receipt Print', label: 'Receipt 04', recommendedSize: 12 },
  { value: 'Merchant Copy Wide', label: 'Receipt 05', recommendedSize: 12 },
  { value: 'Dot Array', label: 'Receipt 06', recommendedSize: 12 },
];

// Get recommended size for current font
const getRecommendedSize = (fontValue: string) => {
  const font = FONTS.find(f => f.value === fontValue);
  return font?.recommendedSize || 12;
};

const PAPER_STYLES: { value: PaperStyle; label: string }[] = [
  { value: 'thermal', label: 'Thermal' },
  { value: 'plain', label: 'Plain White' },
  { value: 'new', label: 'New Paper' },
  { value: 'ruffled', label: 'Ruffled' },
  { value: 'used', label: 'Used' },
  { value: 'worn', label: 'Worn' },
  { value: 'old', label: 'Old' },
];

const CURRENCIES: { value: CurrencyFormat; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: '$', label: 'USD ($)' },
  { value: '€', label: 'EUR (€)' },
  { value: '£', label: 'GBP (£)' },
  { value: '¥', label: 'JPY/CNY (¥)' },
  { value: '₹', label: 'INR (₹)' },
];

export function SettingsEditor() {
  const { isPaid } = useAuth();
  const { open: openUpgrade } = useUpgrade();
  const { template, updateSettings } = useReceiptBuilder();
  const { settings } = template;

  return (
    <div className="space-y-6 pb-20">
      {/* Receipt Width */}
      <div className="space-y-2">
        <Label htmlFor="width">Receipt Width (px)</Label>
        <div className="flex items-center gap-4">
          <Slider
            id="width"
            min={200}
            max={400}
            step={10}
            value={[settings.width]}
            onValueChange={([value]) => updateSettings({ width: value })}
            className="flex-1"
          />
          <span className="w-12 text-sm text-muted-foreground">
            {settings.width}px
          </span>
        </div>
        <p className="text-xs text-muted-foreground">Standard: 280px (80mm thermal paper)</p>
      </div>

      {/* Font */}
      <div className="space-y-2">
        <Label>Font Family</Label>
        <Select
          value={normalizeFont(settings.font)}
          onValueChange={(value) => updateSettings({ font: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONTS.map((font) => (
              <SelectItem key={font.value} value={font.value}>
                <span style={{ fontFamily: font.value }}>{font.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Font Size */}
      <div className="space-y-2">
        <Label htmlFor="fontSize">Base Font Size (px)</Label>
        <div className="flex items-center gap-4">
          <Slider
            id="fontSize"
            min={4}
            max={18}
            step={1}
            value={[settings.fontSize]}
            onValueChange={([value]) => updateSettings({ fontSize: value })}
            className="flex-1"
          />
          <span className="w-12 text-sm text-muted-foreground">
            {settings.fontSize}px
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Recommended for this font: {getRecommendedSize(settings.font)}px
        </p>
      </div>

      {/* Paper Style */}
      <div className="space-y-2">
        <Label>Paper Style</Label>
        <Select
          value={settings.paperStyle}
          onValueChange={(value: PaperStyle) =>
            updateSettings({ paperStyle: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAPER_STYLES.map((style) => (
              <SelectItem key={style.value} value={style.value}>
                {style.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Line Spacing */}
      <div className="space-y-2">
        <Label>Line Spacing</Label>
        <Select
          value={settings.spacing || 'spaced'}
          onValueChange={(value: SpacingStyle) =>
            updateSettings({ spacing: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="spaced">Spaced (Default)</SelectItem>
            <SelectItem value="narrow">Narrow (Compact)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {settings.spacing === 'narrow' 
            ? 'Tight spacing like real thermal receipts' 
            : 'Standard spacing with more room between lines'}
        </p>
      </div>

      {/* Colors */}
      <div className="grid grid-cols-1 builder-desktop:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="bgColor">Background</Label>
          <div className="flex gap-2">
            <Input
              id="bgColor"
              type="color"
              value={settings.backgroundColor}
              onChange={(e) =>
                updateSettings({ backgroundColor: e.target.value })
              }
              className="w-12 h-9 p-1 cursor-pointer"
            />
            <Input
              value={settings.backgroundColor}
              onChange={(e) =>
                updateSettings({ backgroundColor: e.target.value })
              }
              className="flex-1"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="textColor">Text Color</Label>
          <div className="flex gap-2">
            <Input
              id="textColor"
              type="color"
              value={settings.textColor}
              onChange={(e) => updateSettings({ textColor: e.target.value })}
              className="w-12 h-9 p-1 cursor-pointer"
            />
            <Input
              value={settings.textColor}
              onChange={(e) => updateSettings({ textColor: e.target.value })}
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Currency */}
      <div className="grid grid-cols-1 builder-desktop:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Currency</Label>
          <Select
            value={settings.currency}
            onValueChange={(value: CurrencyFormat) =>
              updateSettings({ currency: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((currency) => (
                <SelectItem key={currency.value} value={currency.value}>
                  {currency.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Position</Label>
          <Select
            value={settings.currencyPosition}
            onValueChange={(value: 'before' | 'after') =>
              updateSettings({ currencyPosition: value })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="before">Before {settings.currency !== 'none' ? `(${settings.currency}100)` : ''}</SelectItem>
              <SelectItem value="after">After {settings.currency !== 'none' ? `(100${settings.currency})` : ''}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Watermark */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label htmlFor="watermark">Show Watermark</Label>
            {!isPaid && (
              <span className="rounded-full bg-[#E7F5FF] border border-[#7fbdf2] px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#0b4f92]">
                PRO
              </span>
            )}
          </div>
          {isPaid ? (
            <Switch
              id="watermark"
              checked={settings.showWatermark}
              onCheckedChange={(checked) =>
                updateSettings({ showWatermark: checked })
              }
            />
          ) : (
            // Disabled controls swallow clicks, so the wrapper carries the
            // handler and the switch is left inert underneath it.
            <span
              role="button"
              tabIndex={0}
              aria-label="Turning the watermark off is a Pro feature"
              onClick={() => openUpgrade('Turning the watermark off is a Pro feature.')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openUpgrade('Turning the watermark off is a Pro feature.');
                }
              }}
              className="cursor-pointer"
            >
              <Switch id="watermark" checked disabled className="pointer-events-none" />
            </span>
          )}
        </div>
        {isPaid ? (
          settings.showWatermark && (
            <p className="text-xs text-muted-foreground">
              Watermark: TryReceiptMaker.com
            </p>
          )
        ) : (
          <p className="text-xs text-muted-foreground">
            Free downloads include the TryReceiptMaker.com watermark.{' '}
            <button
              type="button"
              onClick={() => openUpgrade('Turning the watermark off is a Pro feature.')}
              className="text-[#0068D6] font-medium underline decoration-transparent underline-offset-2 transition-colors hover:decoration-[#0068D6]"
            >
              Upgrade to remove it
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
